import React, { useState, useEffect } from 'react';
import {Trophy } from 'lucide-react';

import { QUOTES_DATA} from './data/quotes.js';
import { CHARACTERS, ADVANCEMENTS, BOOKS } from './data/gameOptions.js';

const QuoteWordleGame = () => {
  const [currentQuote, setCurrentQuote] = useState(null);
  const [guesses, setGuesses] = useState({ character: '', advancement: '', book: '' });
  const [correctAnswers, setCorrectAnswers] = useState({ character: false, advancement: false, book: false });
  const [guessHistory, setGuessHistory] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  
  // Character autocomplete states
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredCharacters, setFilteredCharacters] = useState([]);

  const maxAttempts = 6;
  const maxHistoryDisplay = 5;


  // Get quote index based on days since epoch
  const getDailyQuoteIndex = () => {
    const startDate = new Date('2025-10-15'); // Start date for the quote cycle
    const today = new Date();
    const diffTime = Math.abs(today - startDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays % QUOTES_DATA.length;
  };

  const startNewGame = () => {
    const quoteIndex = getDailyQuoteIndex();
    const todaysQuote = QUOTES_DATA[quoteIndex];
    setCurrentQuote(todaysQuote);
    setGuesses({ character: '', advancement: '', book: '' });
    setCorrectAnswers({ character: false, advancement: false, book: false });
    setGuessHistory([]);
    setAttempts(0);
    setGameWon(false);
    setGameOver(false);
    setShowSuggestions(false);
  };

    useEffect(() => {
    startNewGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleCharacterInputChange = (value) => {
    if (correctAnswers.character) return; 
    setGuesses(prev => ({ ...prev, character: value }));
    
    if (value.trim()) {
      const filtered = CHARACTERS.filter(char =>
        char.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8); // Limit to 8 suggestions
      setFilteredCharacters(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectCharacter = (character) => {
    setGuesses(prev => ({ ...prev, character }));
    setShowSuggestions(false);
  };

  const handleInputChange = (field, value) => {
    if (correctAnswers[field]) return; 
    setGuesses(prev => ({ ...prev, [field]: value }));
  };

  const isCorrect = (guess, answer) => {
    return guess.toLowerCase().trim() === answer.toLowerCase().trim();
  };

  const getDirectionHint = (list, guess, answer) => {
    const guessIndex = list.findIndex(item => item.toLowerCase() === guess.toLowerCase());
    const answerIndex = list.findIndex(item => item.toLowerCase() === answer.toLowerCase());

    if (guessIndex === -1 || answerIndex === -1) return null;

    if (guessIndex < answerIndex) return "⬆️ Later";
    if (guessIndex > answerIndex) return "⬇️ Earlier";
    return null;
  };

     const submitGuess = () => {
    if (!currentQuote || gameWon || gameOver) return;

    const requiredFields = ['character', 'advancement', 'book'].filter(field => !correctAnswers[field]);
    const hasAllRequired = requiredFields.every(field => guesses[field].trim());

    if (!hasAllRequired) return;

    const results = {
      character: correctAnswers.character || isCorrect(guesses.character, currentQuote.character),
      advancement: correctAnswers.advancement || isCorrect(guesses.advancement, currentQuote.advancement),
      book: correctAnswers.book || isCorrect(guesses.book, currentQuote.book)
    };

    const directionHints = {
      advancement: !results.advancement
        ? getDirectionHint(ADVANCEMENTS, guesses.advancement, currentQuote.advancement)
        : null,
      book: !results.book
        ? getDirectionHint(BOOKS, guesses.book, currentQuote.book)
        : null
    };

    const newGuess = {
      attempt: attempts + 1,
      guesses: { ...guesses },
      results,
      directionHints
    };

    setGuessHistory(prev => [newGuess, ...prev].slice(0, maxHistoryDisplay));
    setAttempts(prev => prev + 1);

    const newCorrectAnswers = { ...correctAnswers };
    if (results.character && !correctAnswers.character) newCorrectAnswers.character = true;
    if (results.advancement && !correctAnswers.advancement) newCorrectAnswers.advancement = true;
    if (results.book && !correctAnswers.book) newCorrectAnswers.book = true;

    setCorrectAnswers(newCorrectAnswers);

    if (results.character && results.advancement && results.book) {
      setGameWon(true);
      return;
    } else if (attempts + 1 >= maxAttempts) {
      setGameOver(true);
      return;
    }

    const newGuesses = { ...guesses };
    if (!results.character && !correctAnswers.character) newGuesses.character = '';
    if (!results.advancement && !correctAnswers.advancement) newGuesses.advancement = '';
    if (!results.book && !correctAnswers.book) newGuesses.book = '';

    setGuesses(newGuesses);
    setShowSuggestions(false);
  };

const getInputStyle = (field) => {
    // Always show green if this field is correct
    console.log(correctAnswers[field])
    if (correctAnswers[field]) {
      return 'bg-green-100 border-green-500 text-green-800 cursor-not-allowed';
    }
    
    // Show red if this field was just guessed incorrectly
    if (guessHistory.length > 0) {
      const lastGuess = guessHistory[0];
      // Only show red if the field was attempted in the last guess AND was wrong
      if (lastGuess.guesses[field] && lastGuess.guesses[field].trim() !== '' && !lastGuess.results[field]) {
        return 'bg-red-100 border-red-500 text-red-800';
      }
    }
    
    // Default styling
    return 'bg-white border-gray-300';
  };
  
  const getHistoryItemStyle = (isCorrectGuess) => {
    return isCorrectGuess
      ? 'bg-green-500 text-white'
      : 'bg-red-500 text-white';
  };

  if (!currentQuote) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-600 p-4">
      <div className="max-w-4xl mx-auto">
        <header className="text-center mb-8">
          <h1 className="text-5xl font-bold text-white mb-2 tracking-wide">Information Requested</h1>
          <p className="text-blue-200 text-lg">CRADLERDLE</p>
          <p className="text-blue-300 text-sm mt-1">
            Quote #{getDailyQuoteIndex() + 1} • {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Game Panel */}
          <div className="lg:col-span-2 bg-sky-950 rounded-xl shadow-2xl p-6">
            <div className="text-center mb-6">
              <blockquote className="text-xl italic text-red-700 mb-4 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-l-4 border-blue-500">
                "{currentQuote.quote}"
              </blockquote>
            </div>

            <div className="space-y-4">
              {/* Character Input with Autocomplete */}
              <div className="relative">
                <label className="block text-sm font-semibold text-white mb-2">
                  Character 
                </label>
                <input
                  type="text"
                  value={guesses.character}
                  onChange={(e) => handleCharacterInputChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className={`w-full p-4 border-2 rounded-lg text-lg font-medium ${getInputStyle('character')}`}
                  placeholder="Who said this quote?"
                  disabled={gameWon || gameOver}
                />
                
                {showSuggestions && filteredCharacters.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredCharacters.map((character, index) => (
                      <button
                        key={index}
                        onClick={() => selectCharacter(character)}
                        className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                      >
                        {character}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Advancement Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Advancement Level
                </label>
                <select
                  value={guesses.advancement}
                  onChange={(e) => handleInputChange('advancement', e.target.value)}
                  className={`w-full p-4 border-2 rounded-lg text-lg font-medium ${getInputStyle('advancement')}`}
                  disabled={gameWon || gameOver}
                >
                  <option value="">Select advancement level...</option>
                  {ADVANCEMENTS.map((advancement) => (
                    <option key={advancement} value={advancement}>
                      {advancement}
                    </option>
                  ))}
                </select>
              </div>

              {/* Book Dropdown */}
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Book Title
                </label>
                <select
                  value={guesses.book}
                  onChange={(e) => handleInputChange('book', e.target.value)}
                  className={`w-full p-4 border-2 rounded-lg text-lg font-medium ${getInputStyle('book')}`}
                  disabled={gameWon || gameOver}
                >
                  <option value="">Select book...</option>
                  {BOOKS.map((book) => (
                    <option key={book} value={book}>
                      {book}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-between items-center">
              <div className="text-lg font-semibold text-white">
                Attempts: {attempts} / {maxAttempts}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={submitGuess}
                  disabled={gameWon || gameOver || (() => {
                    const requiredFields = ['character', 'advancement', 'book'].filter(field => !correctAnswers[field]);
                    return !requiredFields.every(field => guesses[field].trim());
                  })()}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-lg font-semibold text-lg transition-all transform hover:scale-105 disabled:scale-100"
                >
                  Submit Guess
                </button>
              </div>
            </div>

            {gameWon && (
              <div className="mt-6 p-6 bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300 rounded-xl text-center">
                <div className="flex items-center justify-center gap-3 text-green-800 font-bold text-2xl mb-2">
                  <Trophy size={32} />
                  Congratulations!
                </div>
                <p className="text-green-700 text-lg">You guessed it in {attempts} attempt{attempts !== 1 ? 's' : ''}!</p>
              </div>
            )}

            {gameOver && !gameWon && (
              <div className="mt-6 p-6 bg-gradient-to-r from-red-100 to-rose-100 border-2 border-red-300 rounded-xl">
                <h3 className="font-bold text-red-800 text-xl mb-3">Game Over - Correct Answers:</h3>
                <div className="text-red-700 space-y-2 text-lg">
                  <p><strong>Character:</strong> {currentQuote.character}</p>
                  <p><strong>Advancement:</strong> {currentQuote.advancement}</p>
                  <p><strong>Book:</strong> {currentQuote.book}</p>
                </div>
              </div>
            )}
          </div>

          {/* Guess History Panel */}
          <div className="bg-white rounded-xl shadow-2xl p-6">
            <h3 className="font-bold text-gray-800 text-xl mb-4 text-center">Recent Guesses</h3>

            {guessHistory.length === 0 ? (
              <p className="text-gray-500 text-center italic">No guesses yet</p>
            ) : (
              <div className="space-y-3">
                {guessHistory.map((guess, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border">
                    <div className="text-sm font-semibold text-gray-600 mb-2">
                      Attempt #{guess.attempt}
                    </div>
                    <div className="space-y-2">
                      <div className={`p-2 rounded text-sm font-medium ${getHistoryItemStyle(guess.results.character)}`}>
                        <strong>Character:</strong> {guess.guesses.character}
                      </div>
                      <div className={`p-2 rounded text-sm font-medium ${getHistoryItemStyle(guess.results.advancement)}`}>
                        <strong>Advancement:</strong> {guess.guesses.advancement}
                        {!guess.results.advancement && guess.directionHints.advancement && (
                          <span className="ml-2 text-xs italic">{guess.directionHints.advancement}</span>
                        )}
                      </div>
                      <div className={`p-2 rounded text-sm font-medium ${getHistoryItemStyle(guess.results.book)}`}>
                        <strong>Book:</strong> {guess.guesses.book}
                        {!guess.results.book && guess.directionHints.book && (
                          <span className="ml-2 text-xs italic">{guess.directionHints.book}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-grey bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 justify-center items-center">
          <h3 className="font-bold text-white text-xl mb-3 justify-center">How to Play:</h3>
          <ul className="text-blue-100 space-y-2">
            <li>Read the daily quote and try to guess the character, advancement level, and book</li>
            <li>Character may be a name, or it may be a creature or title if the character is unnamed, eg black dragon</li>
            <li>If advancement or book are incorrect, you will get arrows indicating earlier/later in advancement or book respectively</li>
            <li>Some characters like Ziel have fuzzy advancement, I'll use my best guess, don't kill me</li>
            <li>Dross tier includes Dross, as well as other similar entities not strictly categorizable</li>
            <li>You have {maxAttempts} attempts to guess correctly</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QuoteWordleGame;